import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { money } from '@/lib/money'
import type { Product } from '@/types'
import ProductImage from '@/components/order/ProductImage'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'

function priceRange(p: Product): string {
  const prices = (p.tier_prices ?? []).map((r) => r.price)
  if (prices.length === 0) return '-'
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  return min === max ? money(min) : `${money(min)} - ${money(max)}`
}

/** The product list; a product is created and edited on its own page (ProductEditor). */
export default function AdminProducts() {
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const products = useQuery({
    queryKey: ['admin-products'],
    queryFn: async () => (await api.get<{ data: Product[] }>('/admin/products')).data.data,
  })

  // The product the status switch was tapped on, waiting for confirmation.
  const [statusChange, setStatusChange] = useState<Product | null>(null)

  const setStatus = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) =>
      (await api.patch<{ message: string }>(`/admin/products/${id}/status`, { active })).data,
    onSuccess: (res) => {
      for (const key of ['admin-products', 'products', 'products-picker', 'storefront-products', 'storefront-product']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success(res.message)
      setStatusChange(null)
    },
    onError: (err) => {
      setStatusChange(null)
      toast.error('Could not change the status', errorMessage(err))
    },
  })

  if (products.isLoading) return <LogoLoader />

  const edit = (p: Product) => navigate(`/admin/products/${p.id}`)

  return (
    <div>
      <PageHeader
        title="Product setup"
        subtitle="Configure each product's sizes, garment options and personalization"
        actions={
          <Button onClick={() => navigate('/admin/products/new')}>
            <Plus className="size-4" /> New product
          </Button>
        }
      />

      <Table columns={['', 'Product', 'Sales', 'Category', 'RM/pc', 'Sizes', 'Personalization', 'Status', actionsColumn]}>
        {(products.data?.length ?? 0) === 0 && <EmptyRow colSpan={9} />}
        {products.data?.map((p) => (
          <Row key={p.id} onClick={() => edit(p)}>
            <Cell className="w-14">
              <ProductImage product={p} className="size-10" />
            </Cell>
            <Cell className="font-semibold">{p.name}</Cell>
            {/* All-time, settled orders only, delivery fees left out - the Sales report's own counting. */}
            <Cell className="whitespace-nowrap">
              {p.sales && p.sales.pieces > 0 ? (
                <span className="text-xs font-semibold text-foreground">{money(p.sales.revenue)}</span>
              ) : (
                <span className="text-xs text-muted">No sales yet</span>
              )}
            </Cell>
            <Cell>{p.category}</Cell>
            <Cell className="whitespace-nowrap text-xs font-semibold">{priceRange(p)}</Cell>
            <Cell className="text-xs">{p.sizes.join(', ')}</Cell>
            <Cell>{p.options?.allow_personalization ? <Badge tone="brand">name & no</Badge> : <span className="text-muted">-</span>}</Cell>
            <Cell>
              <button
                type="button"
                role="switch"
                aria-checked={p.active}
                aria-label={`${p.name} is ${p.active ? 'active' : 'inactive'}`}
                disabled={setStatus.isPending}
                onClick={(e) => {
                  e.stopPropagation()
                  setStatusChange(p)
                }}
                className="-m-2 inline-flex items-center gap-2 whitespace-nowrap rounded-lg p-2 disabled:opacity-60"
              >
                <span
                  className={cn(
                    'relative h-5 w-9 shrink-0 rounded-full transition-colors',
                    p.active ? 'bg-success' : 'bg-foreground/25',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 size-4 rounded-full bg-white shadow-sm transition-all',
                      p.active ? 'left-[1.125rem]' : 'left-0.5',
                    )}
                  />
                </span>
                <span className={cn('text-xs font-semibold', p.active ? 'text-status-success' : 'text-muted')}>
                  {p.active ? 'Active' : 'Inactive'}
                </span>
              </button>
            </Cell>
            <Cell className={actionsCellClass}>
              <Button
                variant="ghost"
                className="h-8 px-2"
                onClick={(e) => {
                  e.stopPropagation()
                  edit(p)
                }}
              >
                <Pencil className="size-4" />
              </Button>
            </Cell>
          </Row>
        ))}
      </Table>

      <ConfirmDialog
        open={!!statusChange}
        onClose={() => setStatusChange(null)}
        onConfirm={() => statusChange && setStatus.mutate({ id: statusChange.id, active: !statusChange.active })}
        title={statusChange ? `${statusChange.active ? 'Deactivate' : 'Activate'} ${statusChange.name}?` : ''}
        confirmLabel={statusChange?.active ? 'Deactivate' : 'Activate'}
        variant={statusChange?.active ? 'danger' : 'primary'}
        loading={setStatus.isPending}
      >
        {statusChange?.active
          ? 'Customers will no longer find or open it in the shop, and it is left out of the manual order form. Orders already placed are not affected, and you can activate it again at any time.'
          : 'It shows in the shop again, and can be picked on the manual order form.'}
      </ConfirmDialog>
    </div>
  )
}
